// src/components/HeroCanvas.jsx
// Animated trading background — candlesticks, equity curves, floating tickers
// Runs on canvas for smooth performance. Respects prefers-reduced-motion.
import { useEffect, useRef } from 'react';

const GREEN  = 'rgba(0,229,160,';
const RED    = 'rgba(255,77,109,';
const MUTED  = 'rgba(136,136,168,';

// ── TICKERS ──────────────────────────────────────────────────────
const TICKER_LABELS = [
  '+2.4%', '1.3R', '-0.8%', '68% WR', '+$240', '2.1R',
  '-1.2%', '+4.1%', '0.9R', '82 ODF', '+$180', '3.2R',
  '-0.4%', '+1.8%', '71% WR', '+$95', '1.5R', '-$120',
  '+3.6%', '0.7R', '+$310', '55% WR', '2.4R', '-0.3%',
];

function initTicker(w, h, i) {
  return {
    x: Math.random() * w,
    y: h * 0.2 + Math.random() * h * 0.7,
    vy: -(0.18 + Math.random() * 0.22),
    alpha: 0,
    maxAlpha: 0.12 + Math.random() * 0.10,
    label: TICKER_LABELS[i % TICKER_LABELS.length],
    size: 9 + Math.random() * 4,
    positive: TICKER_LABELS[i % TICKER_LABELS.length].startsWith('+'),
    life: 0,
    maxLife: 200 + Math.random() * 160,
  };
}

// ── CANDLESTICKS ──────────────────────────────────────────────────
function initCandles(w, h) {
  const candles = [];
  const count   = Math.floor(w / 22) + 10;
  for (let i = 0; i < count; i++) {
    const bullish = Math.random() > 0.45;
    const bodyH   = 6 + Math.random() * 18;
    const mid     = h * 0.55 + (Math.random() - 0.5) * h * 0.28;
    candles.push({
      x:       w + i * 22,
      bodyY:   mid - bodyH / 2,
      bodyH,
      wickTop:    mid - bodyH / 2 - Math.random() * 8,
      wickBottom: mid + bodyH / 2 + Math.random() * 8,
      bullish,
      alpha: 0.06 + Math.random() * 0.07,
    });
  }
  return candles;
}

// ── EQUITY CURVES ─────────────────────────────────────────────────
function initCurve(w, h, index) {
  // Generate a semi-realistic equity curve path
  const points = [];
  const steps  = 80;
  let   y      = h * (0.45 + index * 0.12);
  for (let i = 0; i <= steps; i++) {
    const drift = (Math.random() - 0.44) * 3.5;
    y = Math.max(h * 0.15, Math.min(h * 0.85, y + drift));
    points.push({ x: (i / steps) * w, y });
  }
  return {
    points,
    alpha:    0.06 + index * 0.02,
    color:    index % 2 === 0 ? GREEN : MUTED,
    offsetX:  0,
    speed:    0.12 + index * 0.05,
    width:    1 + index * 0.3,
  };
}

export default function HeroCanvas() {
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);

  useEffect(() => {
    // Respect reduced motion preference
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let w, h, tickers, candles, curves, frame = 0;

    function resize() {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width  = w;
      canvas.height = h;
      tickers = Array.from({ length: 18 }, (_, i) => initTicker(w, h, i));
      candles = initCandles(w, h);
      curves  = [0, 1, 2].map(i => initCurve(w, h, i));
    }

    function drawCandles() {
      candles.forEach(c => {
        const col = c.bullish ? GREEN : RED;
        // Wick
        ctx.beginPath();
        ctx.strokeStyle = col + c.alpha + ')';
        ctx.lineWidth = 1;
        ctx.moveTo(c.x + 4, c.wickTop);
        ctx.lineTo(c.x + 4, c.wickBottom);
        ctx.stroke();
        // Body
        ctx.fillStyle = col + (c.alpha * 1.4) + ')';
        ctx.fillRect(c.x, c.bodyY, 8, c.bodyH);
        // Move left
        c.x -= 0.35;
        // Reset when off screen
        if (c.x < -12) {
          const bullish = Math.random() > 0.45;
          const bodyH   = 6 + Math.random() * 18;
          const mid     = h * 0.55 + (Math.random() - 0.5) * h * 0.28;
          c.x           = w + 12;
          c.bodyY       = mid - bodyH / 2;
          c.bodyH       = bodyH;
          c.wickTop     = mid - bodyH / 2 - Math.random() * 8;
          c.wickBottom  = mid + bodyH / 2 + Math.random() * 8;
          c.bullish     = bullish;
        }
      });
    }

    function drawCurves() {
      curves.forEach(curve => {
        const pts = curve.points;
        ctx.beginPath();
        ctx.strokeStyle = curve.color + curve.alpha + ')';
        ctx.lineWidth   = curve.width;
        ctx.setLineDash([]);

        const shift = (curve.offsetX % w);
        ctx.beginPath();
        for (let i = 0; i < pts.length; i++) {
          const x = (pts[i].x - shift + w) % w;
          const y = pts[i].y;
          if (i === 0) ctx.moveTo(x, y);
          else         ctx.lineTo(x, y);
        }
        ctx.stroke();

        // Gradient fade on left and right edges
        const fadeW = 80;
        const gradL = ctx.createLinearGradient(0, 0, fadeW, 0);
        gradL.addColorStop(0, 'rgba(10,10,15,1)');
        gradL.addColorStop(1, 'rgba(10,10,15,0)');
        ctx.fillStyle = gradL;
        ctx.fillRect(0, 0, fadeW, h);

        const gradR = ctx.createLinearGradient(w - fadeW, 0, w, 0);
        gradR.addColorStop(0, 'rgba(10,10,15,0)');
        gradR.addColorStop(1, 'rgba(10,10,15,1)');
        ctx.fillStyle = gradR;
        ctx.fillRect(w - fadeW, 0, fadeW, h);

        curve.offsetX += curve.speed;
      });
    }

    function drawTickers() {
      ctx.font = '600 11px "DM Mono", monospace';
      tickers.forEach((t, i) => {
        // Fade in
        if (t.life < 30)       t.alpha = (t.life / 30) * t.maxAlpha;
        // Fade out near end
        else if (t.life > t.maxLife - 40) t.alpha = ((t.maxLife - t.life) / 40) * t.maxAlpha;
        else                   t.alpha = t.maxAlpha;

        const col = t.positive ? GREEN : RED;
        ctx.fillStyle = col + t.alpha + ')';
        ctx.fillText(t.label, t.x, t.y);

        t.y    += t.vy;
        t.life += 1;

        if (t.life >= t.maxLife) {
          Object.assign(t, initTicker(w, h, i));
          t.x   = Math.random() * w;
          t.y   = h * 0.85 + Math.random() * 60;
          t.life = 0;
        }
      });
    }

    function drawFades() {
      // Top fade — strong, keeps content readable
      const gradTop = ctx.createLinearGradient(0, 0, 0, h * 0.35);
      gradTop.addColorStop(0,   'rgba(10,10,15,0.96)');
      gradTop.addColorStop(1,   'rgba(10,10,15,0)');
      ctx.fillStyle = gradTop;
      ctx.fillRect(0, 0, w, h * 0.35);

      // Bottom fade
      const gradBot = ctx.createLinearGradient(0, h * 0.65, 0, h);
      gradBot.addColorStop(0,   'rgba(10,10,15,0)');
      gradBot.addColorStop(1,   'rgba(10,10,15,0.98)');
      ctx.fillStyle = gradBot;
      ctx.fillRect(0, h * 0.65, w, h * 0.35);

      // Centre fade — keeps headline area clean
      const gradCentre = ctx.createRadialGradient(w/2, h/2, 60, w/2, h/2, w * 0.45);
      gradCentre.addColorStop(0,   'rgba(10,10,15,0.75)');
      gradCentre.addColorStop(1,   'rgba(10,10,15,0)');
      ctx.fillStyle = gradCentre;
      ctx.fillRect(0, 0, w, h);
    }

    function animate() {
      ctx.clearRect(0, 0, w, h);
      frame++;

      drawCurves();
      drawCandles();
      drawTickers();
      drawFades();

      rafRef.current = requestAnimationFrame(animate);
    }

    resize();
    animate();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
}
